// src/services/firebase.js
import { initializeApp } from 'firebase/app';
import {
    getFirestore, collection, doc, getDocs, getDoc, query, orderBy, where, serverTimestamp, increment, limit,
} from 'firebase/firestore';
// Writes wait for the member's Firestore identity (adr-firestore-identity.md a2).
import {
    setDoc, addDoc, updateDoc, deleteDoc, runTransaction,
} from './firestoreWrite';
import { trainerDayKey, previousDayKey } from '../utils/timezone';
import { serverNow } from './serverClock';
// Safe: milestones.js depends only on utils/timezone — no import cycle back here.
import { isClassCompletion } from './milestones';

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

let db = null;
let firebaseEnabled = false;

if (firebaseConfig.apiKey && !firebaseConfig.apiKey.includes('REPLACE')) {
    try {
        const app = initializeApp(firebaseConfig);
        db = getFirestore(app);
        firebaseEnabled = true;
    } catch (e) { console.error('Firebase init error:', e); }
}

export { db, firebaseEnabled };

// ═══════════════════════════════════════════════════════════════
//  SITE CONTENT (landing page — all editable in admin)
// ═══════════════════════════════════════════════════════════════
const DEFAULT_SITE_CONTENT = {
    heroTagline: 'The 5 to 20 Method',
    heroTitle: 'moments of movement',
    heroSubtitle: 'with Danielle',
    heroDescription: "Short, effective workouts designed to fit into real life — between meetings, during nap times, or right before bed. Just press play and follow along.",
    featuresHeading: 'Fitting in moments of movement is the goal.',
    featuresSubheading: 'Each day is short, effective, and beginner-friendly — so you never have to wonder if you\'re doing it "right."',
    tile1Title: 'About Me',
    tile1Desc: 'Hey! I\'m Danielle — a mom, fitness lover, and coach who believes that any movement counts.',
    tile1Icon: '',
    tile1Link: '',
    tile2Title: 'Join Me on TikTok',
    tile2Desc: 'Follow along for daily tips, quick workouts, and behind-the-scenes.',
    tile2Icon: '',
    tile2Link: '',
    tile3Title: 'My Philosophy',
    tile3Desc: 'Consistency over intensity. Every moment of movement matters.',
    tile3Icon: '',
    tile3Link: '',
    methodDescription: 'Start with just 5 minutes. As it becomes habit, build to 10, then 20. No pressure, no perfection — just movement that fits your life.',
    methodLabel: 'The Method',
    methodTitle: 'The 5 to 20 Method',
    methodStat1Num: '5',
    methodStat1Label: 'min to start',
    methodStat2Num: '10',
    methodStat2Label: 'min to grow',
    methodStat3Num: '20',
    methodStat3Label: 'min to thrive',
    methodStat4Num: '',
    methodStat4Label: '',
    ctaHeading: 'Join the Movement',
    ctaSubheading: 'Sign up to access programs, free workouts, and more.',
    // ── Landing "Peek Inside" preview section (sits directly under the hero) ──
    // Shows off real in-app content — free Shorts you can play, plus a locked
    // on-demand class library — to entice sign-ups. All copy admin-editable.
    landingPreviewEnabled: true,
    landingPreviewShortsHeading: 'Free workouts you can start today',
    landingPreviewShortsSubcopy: 'Short, follow-along sessions — no membership needed. Tap any to play.',
    landingPreviewLibraryHeading: 'Plus the full class library',
    landingPreviewLibrarySubcopy: 'Every live class becomes an on-demand replay you can return to anytime — unlocked with membership.',
    landingPreviewLockedLabel: 'Members',
    // Navigation & buttons
    dashboardNavLabel: 'Dashboard',
    goToDashboardLabel: 'Go to Dashboard',
    // Testimonials
    testimonialsHeading: 'Testimonials',
    testimonialsSubheading: 'Real results from real women.',
    testimonial1Text: '',
    testimonial1Name: '',
    testimonial1Detail: '',
    testimonial2Text: '',
    testimonial2Name: '',
    testimonial2Detail: '',
    testimonial3Text: '',
    testimonial3Name: '',
    testimonial3Detail: '',
    testimonial4Text: '',
    testimonial4Name: '',
    testimonial4Detail: '',
    testimonial5Text: '',
    testimonial5Name: '',
    testimonial5Detail: '',
    // Dashboard page
    dashboardTitle: 'Welcome back!',
    dashboardSubtitle: 'Your fitness hub — everything in one place.',
    // Member-aware variant of the hero subtitle. Shown to anyone with full
    // access (admin/friend) or an active subscription. Falls through to
    // `dashboardSubtitle` if cleared. This is the *quiet acknowledgment*
    // half of the Dashboard member-recognition pass — see commit notes.
    dashboardMemberSubtitle: "Your membership is active — here's how your week is shaping up.",
    // Single coach avatar image used in the Dashboard "from Danielle"
    // CoachPosts section. Empty falls back to the existing ★ gradient mark.
    // Admin manages this once at NotificationsTab → Coach Posts.
    coachAvatarUrl: '',
    dashboardPurchasedHeading: 'Continue Your Programs',
    dashboardExploreHeading: 'Explore',
    dashboardGetStartedHeading: 'Ready to get started?',
    dashboardGetStartedDesc: 'Browse our programs to find the perfect fit, or jump into free workouts right away.',
    dashboardGetStartedEmoji: '🌟',
    dashboardProgramsCardTitle: 'Programs',
    dashboardProgramsCardDesc: 'Structured workouts to get real results.',
    dashboardProgramsCardEmoji: '🏋️',
    dashboardFreeCardTitle: 'Free Workouts',
    dashboardFreeCardDesc: 'Quick, free workout videos to get you moving — no program required.',
    dashboardFreeCardEmoji: '🎬',
    // Free Workouts page
    freeWorkoutsTitle: 'Free Workouts',
    freeWorkoutsSubtitle: 'Quick, free workout videos — no program required.',
    // Program view — shown ONLY when the workout list fails to load (transient
    // Bunny/network error) so a load failure never masquerades as an empty program.
    programVideosErrorText: "We couldn't load your workouts just now. Please try again.",
    programVideosRetryLabel: 'Retry',
    programAlreadyEnrolledText: 'Already enrolled?',
    programAlreadyEnrolledLinkLabel: 'Log in to see your program',
    programAlreadyEnrolledButtonLabel: 'Already enrolled? Log in',
    // Completion controls (event-log model — see adr-completions-event-log.md)
    completionLogLabel: 'Log this workout',
    completionLogAgainLabel: 'Log again',
    completionLoggedLabel: 'Logged',
    completionUndoLabel: 'Undo',
    completionLiveManualHint: "Didn't get auto-credited? Log it.",
    // Persistent "Your history" remover controls (see docs/ux/completion-undo.md)
    completionHistoryDisclosureLabel: 'Your history',
    completionRemoveConfirmLabel: 'Remove',
    completionRemoveKeepLabel: 'Keep',
    completionHistoryRemoveLabel: 'Remove',
    // Milestone badges + Wall (see adr-milestone-badges.md). Member-facing copy
    // for the Home shelf, the /milestones page and the community counter.
    milestoneCelebrationEyebrow: 'Milestone reached',
    milestoneCelebrationCloseLabel: 'Keep moving',
    milestonesHeading: 'Your Class Milestones',
    milestonesViewAllLabel: 'View all',
    milestonesPageHeading: 'Class Milestones',
    milestonesPageSubtext: "Every milestone you've reached — and the ones still ahead.",
    milestonesBackLabel: 'Back to home',
    milestonesBestRunTemplate: 'Your best: {count} weeks',
    // Member replies (docs/ux/member-replies.md). Release switch ships OFF:
    // while false every viewer gets the legacy comment thread unchanged.
    memberRepliesEnabled: false,
    commentReplyLabel: 'Reply',
    commentReplyingTo: 'Replying to {name}',
    commentReplyPlaceholder: 'Write a reply…',
    commentViewMoreReplies: 'View {count} more replies',
    commentHideReplies: 'Hide replies',
    commentLinkBlocked: "Links can't be shared in comments. Remove it to post.",
    commentDeletedUndo: 'Comment deleted',
    commentDeleteWithReplies: 'This will also remove {count} replies.',
    commentReportTitle: 'Report this comment?',
    commentReportSubtext: "Danielle's team will take a look.",
    commentReportReason1: 'Unkind or hurtful',
    commentReportReason2: 'Spam or selling',
    commentReportReason3: 'Something else',
    commentReportThanks: 'Thanks for letting us know.',
    commentReportedPlaceholder: 'You reported this comment',
    commentHeldNote: "Only visible to you while it's reviewed.",
    moderationNudge: '{count} comment needs your eye',
    purchaseNudge: '{count} purchase needs your attention',
    programSignInFirstNote: "You'll create your free account (or sign in) first — then pay. It keeps your purchase safely tied to you.",
    milestoneWallHeading: 'The Milestone Wall',
    milestoneWallSubtext: 'Every milestone this community has reached. Cheer someone on.',
    milestoneWallEmpty: 'The first milestones will appear here soon.',
    communityCounterLabel: "Together we've completed",
    communityCounterCaption: 'classes and counting',
    // Streak / challenge surface (Dashboard)
    streakHeldHint: 'Move today to keep your streak alive!',
    showMonthlyChallengeStats: false,
    challengeActiveDaysLabel: '{count} active days this month',
    challengeSessionsLabel: '{count} workouts this month',
    // Series tab (free self-paced challenges) — see docs/ux/series-tab.md.
    // Default-off feature flag: admins always see the Series tab; non-admins
    // see it only once this is flipped true.
    enableSeriesTab: false,
    workoutsTabSeries: 'Series',
    // Workouts tab tier labels — the small eyebrow over each group in the
    // segmented control (Membership = paid pair, Free = free pair).
    workoutsTierMembershipLabel: 'Membership',
    workoutsTierFreeLabel: 'Free',
    // Class Guide — the collapsed "Your Class Guide" disclosure between the tab
    // bar and the content on Schedule + On Demand. See src/components/ClassGuide.js
    // and docs/ux/class-guide-preview.html. The Schedule / On Demand names are NOT
    // repeated here: the guide reads workoutsTabUpcoming / workoutsTabOnDemand so a
    // renamed tab renames itself in the guide. "Ease In" / "Strong+" stay hardcoded
    // (fixed taxonomy, TAG-001).
    showClassGuide: true,
    classGuideTitle: 'Your Class Guide',
    classGuideScheduleHead: 'This is your weekly plan.',
    classGuideScheduleBody: 'Three 15-minute workouts each week, so you always know what to do next. Just follow along!',
    classGuideOnDemandHead: 'More classes, whenever you want them.',
    classGuideOnDemandBody: 'Explore the full workout library anytime. Perfect for off days, extra movement, or simply choosing what feels good that day.',
    classGuideEaseInHead: 'Gentler classes for when you’re easing back in.',
    classGuideEaseInBody: 'A slower pace with more time to learn each movement. A great place to start or come back to anytime.',
    classGuideStrongPlusHead: 'Heavier weights for when you’re ready.',
    classGuideStrongPlusBody: 'More focused strength work using heavier weights, with options to scale back whenever you need.',
    classGuideClassicHead: 'The signature class, just as you know it.',
    classGuideClassicBody: 'Danielle’s classic 15-minute workout at a steady pace. On days with two classes, it’s the one on the left.',
    // Schedule day with exactly two classes (ClassChoicePair, 2026-10-13).
    classChoiceHeading: 'Two ways to move today. Pick one or double up.',
    classChoiceKicker: 'Two options',
    // Reply Assistant (Admin → Replies) — queue chrome + Reply-all confirmation.
    // {count} is substituted at render time. See docs/ux/comment-reply-assistant.md.
    replyQueueSubline: 'Drafted in your voice. Edit anything, then reply.',
    replyQueueEmptyTitle: 'All caught up.',
    replyAllHeading: 'Send {count} replies?',
    replyAllBody: "They'll post as you, and each member will get the usual notification. Anything you've edited sends exactly as you wrote it.",
    replyAllConfirmLabel: 'Send all {count}',
    replyAllBackLabel: 'Back',
    replyNudgeTemplate: '{count} replies ready for you',
    seriesTabHeading: 'Challenges',
    seriesTabSubtext: 'Multi-day movement journeys you can start anytime and do at your own pace.',
    seriesEmptyComingSoon: 'New challenges are coming soon — check back shortly.',
    seriesDayLabelTemplate: 'Day {n}',
    seriesUpNextLabel: 'Up next',
    seriesProgressTemplate: '{done} of {total} days',
    seriesStartCue: 'Start the challenge',
    seriesCompletedLabel: 'Challenge complete',
    // Series detail page — rich content + shareable link.
    // These are the GLOBAL fallbacks: a series may override the two headings on
    // its own doc, so a single challenge can be retitled without affecting the
    // rest. See docs/decisions/adr-series-deep-link-and-sections.md.
    seriesHighlightsHeading: "What's Included",
    seriesFaqHeading: 'Questions',
    seriesShareLabel: 'Share',
    seriesShareCopiedLabel: 'Link copied',
    seriesNotFoundText: "That challenge isn't available right now.",
    // Prefix on a day that has a scheduled release date, e.g. "Unlocks Mon, Sep 15".
    seriesLockedLabel: 'Unlocks',
    // Public (no-account) challenge page. Shown ONLY to signed-out visitors on a
    // challenge whose link has been made public. The copy is an INVITATION, never
    // remediation — nothing is being withheld from them, so the honest pitch is
    // keeping their progress, not gaining access.
    // See docs/decisions/adr-public-series-access.md.
    // NOTE: seriesPublicInviteFreeLine / seriesPublicInviteTrackLine were retired
    // 2026-09-19 with the above-the-days invitation block. Any custom text an
    // admin saved for them stays in the doc, harmlessly unread.
    seriesPublicInviteHeading: 'Want to keep this going?',
    seriesPublicInviteClosingLine: 'Create a free account and your days here start counting toward your streak.',
    seriesPublicCreateAccountLabel: 'Create free account',
    seriesPublicSignInLabel: 'Sign in',
    seriesPublicSignInPrefix: 'Already have one?',
    seriesPublicStartHereLabel: 'Start here',
    // Theme colors (leave empty to use defaults)
    themeCream: '',
    themeCreamDark: '',
    themePrimary: '',
    themePrimaryDark: '',
    themePrimaryLight: '',
    themeCharcoal: '',
    themeWarmGray: '',
    themeSuccess: '',
    themeDanger: '',
    themeLink: '',
    themeLinkHover: '',
    themeCardBg: '',
    themeNavCardBg: '',
    themeTileBg: '',
    themeFilterPillBg: '',
    themeFilterPillText: '',
    themeFilterPillActiveBg: '',
    themeFilterPillActiveText: '',
    // About Me page
    aboutPhoto: '',
    aboutHeadline: '',
    aboutTagline: '',
    aboutIntro: '',
    aboutBody: '',
    aboutClosingHeadline: '',
    aboutClosing: '',
    aboutCtaText: 'Browse Programs',
    aboutCtaLink: '/programs',
    // Theme fonts (leave empty to use defaults)
    themeHeadingFont: '',
    themeBodyFont: '',
    // Legal Content (/terms page) — fully admin-editable. Each field falls
    // through to a hardcoded English default in `src/pages/TermsPage.js` if
    // the value is empty, so the page always renders something legible even
    // if Firestore is unreachable. The Participation Waiver sections preserve
    // the EXACT legal substance of the historical WaiverModal text — same
    // protective scope, delivered as a readable page instead of a scroll-
    // jail modal. See `docs/decisions/adr-waiver-clerk-express-consent.md`.
    termsPageTitle: '',
    termsPageIntro: '',
    waiverParticipationTitle: '',
    waiverParticipationContent: '',
    waiverRiskTitle: '',
    waiverRiskContent: '',
    waiverReleaseTitle: '',
    waiverReleaseContent: '',
    termsOfUseTitle: '',
    termsOfUseContent: '',
    privacyTitle: '',
    privacyContent: '',
    termsLastUpdated: '',
    // ── /join (subscription marketing page) ─────────────────────────────────
    // These are the source-of-truth defaults. `fetchSiteContent` merges them with
    // the admin-saved doc (admin values win, missing keys fall through to here),
    // so first-time admins see these values in /join AND in their admin form.
    // Cleared admin fields (empty strings) WIN over these — clear-to-hide works.
    // JoinPage.js has a parallel DEFAULTS const used as the initial-state shim
    // (pre-fetch render) and as the catch fallback for Firestore failure — keep
    // the two in sync. See `docs/ux/join-marketing-page.md` +
    // `docs/decisions/adr-join-marketing-page.md`.
    joinHeroEyebrow: 'UNLIMITED · WITH DANIELLE',
    joinHeroHeadline: 'Show up for yourself,\non your schedule.',
    joinHeroSubcopy: 'Unlimited live classes plus a growing library of on-demand workouts — all for the price of one boutique class. Move when you can, where you can.',
    joinPrimaryCta: 'Start Moving',
    joinReassurance: 'Cancel anytime · No commitment',
    // Shown in place of BOTH /join Subscribe CTAs (hero + bottom) when the
    // admin Settings → "Subscription Option" toggle is OFF. Mirrors the
    // load-bearing `subscriptionEnabled !== false` semantic used elsewhere
    // (Workouts.js lock calc) — undefined falls through to "enrollment open".
    joinEnrollmentClosedMessage: 'Enrollment opens May 17th @ 11am CT. See you then!',
    // ── Member view (shown to signed-in subscribers in place of the
    // Subscribe CTA, on BOTH hero + bottom). Editorial composition: eyebrow +
    // italic-serif headline + optional sub-copy + single filled primary
    // button. Subcopy defaults empty so the baseline view is a clean three-
    // element composition; admin can add a subline for warmth.
    joinMemberEyebrow: 'MEMBER ACCESS',
    joinMemberHeadline: "You're already moving with us.",
    joinMemberSubcopy: '',
    joinMemberCta: 'Take me to my workouts →',
    joinHeroImage: '',
    joinSocialProof: 'Join women already moving with Danielle',
    joinIncludedHeading: 'Everything you need to move',
    joinIncludedSubheading: 'One subscription. Every class.',
    joinIncluded1Title: 'Live classes every week',
    joinIncluded1Desc: 'Move with Danielle in real time. Pre-recorded options too — whatever fits your week.',
    joinIncluded2Title: 'On-demand library',
    joinIncluded2Desc: 'Every past class becomes a replay you can return to anytime.',
    joinIncluded3Title: 'Short, effective workouts',
    joinIncluded3Desc: 'From 5-minute resets to 60-minute strength sessions. Real-life length.',
    joinIncluded4Title: 'Beginner-friendly',
    joinIncluded4Desc: 'Modifications offered every class. "Ease In" tagged classes for fresh starts.',
    joinIncluded5Title: 'Community built in',
    joinIncluded5Desc: 'See who else is in class, comment together, share the wins.',
    joinIncluded6Title: 'Cancel anytime',
    joinIncluded6Desc: 'Month-to-month. No contracts. No commitments.',
    // Lifestyle photo strips — each strip is a three-tile triptych.
    // Legacy single-image keys retained as backwards-compat fallback for
    // tile A of each strip; see LifestyleTriptych in JoinPage.js.
    joinLifestyleImage1: '',
    joinLifestyleImage2: '',
    joinLifestyle1Image1: '',
    joinLifestyle1Image2: '',
    joinLifestyle1Image3: '',
    joinLifestyle2Image1: '',
    joinLifestyle2Image2: '',
    joinLifestyle2Image3: '',
    joinMeetHeading: "Hey, I'm Danielle.",
    joinMeetBody: "I'm a coach, a mom, and a believer that **any** movement counts. The classes I lead here are the ones I wish I'd had when I was just starting out — short enough to actually do on a busy day, real enough to actually feel.\n\nThis is movement that fits your life, not the other way around.",
    joinMeetImage: '',
    joinTestimonialsHeading: 'Real women, real results',
    joinTestimonialsSubheading: 'What members are saying.',
    joinStatsHeading: 'By the numbers',
    joinStat1Num: '5–60', joinStat1Label: 'minute options',
    joinStat2Num: 'Weekly', joinStat2Label: 'live classes',
    joinStat3Num: 'Anytime', joinStat3Label: 'on-demand access',
    joinFaqHeading: 'Questions?',
    joinFaq1Q: 'Can I cancel anytime?',
    joinFaq1A: "Yes. Cancel anytime from your Account page. You'll keep access through the end of your billing period — no questions asked, no friction.",
    joinFaq2Q: 'What if I miss a live class?',
    joinFaq2A: 'Every live class is recorded automatically. The replay shows up in your On Demand library within a few minutes of class ending.',
    joinFaq3Q: "I'm a total beginner. Is this for me?",
    joinFaq3A: 'Yes. Start with the "Ease In" tagged classes — they\'re designed to build confidence without overwhelm. Modifications are offered every class.',
    joinFaq4Q: 'How much is it?',
    joinFaq4A: "It's a flat monthly rate. No commitments, no contracts, no hidden tiers.",
    joinFaq5Q: 'What equipment do I need?',
    joinFaq5A: 'Most classes are bodyweight only. Some use light dumbbells or resistance bands — gear is always called out in the class description so you can plan ahead.',
    // Overflow slots 6–10 — empty defaults so they stay hidden until admin
    // fills them in. JoinPage's FaqSection drops any row missing Q or A.
    joinFaq6Q: '', joinFaq6A: '',
    joinFaq7Q: '', joinFaq7A: '',
    joinFaq8Q: '', joinFaq8A: '',
    joinFaq9Q: '', joinFaq9A: '',
    joinFaq10Q: '', joinFaq10A: '',
    joinBottomHeadline: 'Ready when you are.',
    joinBottomSubcopy: 'One subscription. Every class. Cancel anytime.',
    // ── Email automations (Flodesk integration) ─────────────────────────────
    // Admin-managed transactional email triggers. Each entry is keyed by event;
    // the stripe-webhook reads this from siteConfig and, when an automation is
    // enabled with a non-empty segmentId, calls Flodesk to add the user to that
    // segment (which triggers the workflow attached to the segment). All entries
    // ship DISABLED with empty segmentIds — admin must explicitly opt in via
    // AdminDashboard > Emails. The `locked: true` entries are V2 placeholders
    // visible in the admin UI but not yet wired in the webhook code.
    // See docs/decisions/adr-email-automations.md + docs/ux/admin-emails-tab.md.
    emailAutomations: {
        subscriptionWelcome: {
            enabled: false,
            flodeskSegmentId: '',
            label: 'Subscription Welcome',
        },
        programWelcomeGeneric: {
            enabled: false,
            flodeskSegmentId: '',
            label: 'Program Welcome',
        },
        subscriptionCancelled: {
            enabled: false,
            flodeskSegmentId: '',
            label: 'Subscription Cancelled',
            locked: true,
        },
        subscriptionPaymentFailed: {
            enabled: false,
            flodeskSegmentId: '',
            label: 'Subscription Payment Failed',
            locked: true,
        },
    },
    // ── Flodesk AUDIENCE segments (distinct from the one-shot welcomes above) ──
    // Three standing lists that people join AND LEAVE as their relationship with the
    // business changes. Kept in step by /api/admin-sync-audiences, which is both the
    // initial backfill and the ongoing drift repair.
    // Ships OFF and empty. Each MUST point at its own brand-new Flodesk segment with
    // NO workflow attached — see docs/decisions/adr-flodesk-audience-segments.md §4.
    audienceSegments: {
        activeMembers: { enabled: false, flodeskSegmentId: '', label: 'Active Members' },
        freeSignups: { enabled: false, flodeskSegmentId: '', label: 'Free Signups' },
        programBuyers: { enabled: false, flodeskSegmentId: '', label: 'Program Buyers' },
    },
};

// ── Two-tier siteConfig cache ──
// Tier 1: localStorage — persists across page loads. Hydrated at module boot
// (before any React render). Lets `getCachedSiteContent()` return real admin
// content synchronously, so the first paint of /join (and theme application
// in index.js) uses admin's customized copy/colors/photos — NOT defaults.
// This eliminates the visible flash of default content → admin content that
// returning visitors used to see.
//
// Tier 2: module-level — 5-min TTL, suppresses Firestore reads within a
// session. Same purpose as before (drops reads from 8+/session to ~1/5min).
//
// Cache invalidation: `updateSiteContent()` clears BOTH tiers. Any admin
// edit immediately invalidates localStorage so the admin's own next page
// load pulls fresh data and re-persists. Other users see the change on
// their next natural revalidation cycle (next 5-min boundary OR next
// hard-refresh, since the stale-while-revalidate fetch always runs in the
// background).
//
// Storage shape: a single JSON blob keyed by `mom_site_content_v1`. The
// `_v1` suffix lets us invalidate ALL caches by bumping the key if we ever
// make a backwards-incompatible schema change.
const SITE_CONTENT_LS_KEY = 'mom_site_content_v1';

let _siteContentCache = null;
let _siteContentCacheTime = 0;
const SITE_CONTENT_TTL = 5 * 60 * 1000; // 5 minutes

// Sync read from localStorage. Safe to call at any time including before
// React mounts. Returns null if no cache, corrupted JSON, or localStorage
// disabled (Safari incognito). Spreads DEFAULT_SITE_CONTENT first so any
// newly-added keys missing from the cached blob fall through to defaults.
export function getCachedSiteContent() {
    try {
        const raw = (typeof localStorage !== 'undefined') ? localStorage.getItem(SITE_CONTENT_LS_KEY) : null;
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;
        return { ...DEFAULT_SITE_CONTENT, ...parsed };
    } catch { return null; }
}

// Module-boot hydration. Seeds the in-memory cache from localStorage so the
// very first `fetchSiteContent()` call in the session can return cached data
// synchronously (the cache-hit branch below). The stale-while-revalidate
// behavior comes from setting `_siteContentCacheTime = 0` — meaning the TTL
// is immediately expired, so the next call still triggers a background
// Firestore fetch to refresh.
(() => {
    const cached = getCachedSiteContent();
    if (cached) {
        _siteContentCache = cached;
        _siteContentCacheTime = 0; // treat as stale → revalidate on first fetch
    }
})();

export async function fetchSiteContent() {
    // Cache-hit + fresh: return instantly, no fetch.
    if (_siteContentCache && (Date.now() - _siteContentCacheTime < SITE_CONTENT_TTL)) return _siteContentCache;
    if (!firebaseEnabled) return _siteContentCache || DEFAULT_SITE_CONTENT;
    try {
        const snap = await getDoc(doc(db, 'siteConfig', 'content'));
        if (snap.exists()) {
            _siteContentCache = { ...DEFAULT_SITE_CONTENT, ...snap.data() };
        } else {
            // Never WRITE here (owner rule 2026-10-06: a member's device must
            // not change shared data). This used to save the factory defaults
            // when the doc read back as missing — one bad read on one phone
            // would have wiped Danielle's whole customised site. Show defaults
            // for this view only, without caching them, so the next call
            // re-reads the real doc. Admin saves use setDoc(merge), which
            // creates the doc if it is ever genuinely absent.
            return _siteContentCache || DEFAULT_SITE_CONTENT;
        }
        _siteContentCacheTime = Date.now();
        // Persist to localStorage so the next page load can paint with real
        // content immediately. Errors swallowed — quota-exceeded / Safari
        // incognito are non-fatal; we just lose the cross-session benefit.
        try {
            if (typeof localStorage !== 'undefined') {
                localStorage.setItem(SITE_CONTENT_LS_KEY, JSON.stringify(_siteContentCache));
            }
        } catch { /* localStorage unavailable */ }
        return _siteContentCache;
    } catch (e) {
        // Network failure: return whatever we have (cached or defaults). Don't
        // throw — callers shouldn't have to deal with the offline path.
        return _siteContentCache || DEFAULT_SITE_CONTENT;
    }
}

export async function updateSiteContent(updates) {
    if (!firebaseEnabled) return false;
    try {
        await setDoc(doc(db, 'siteConfig', 'content'), updates, { merge: true });
        // Invalidate BOTH cache tiers. The admin's own next fetchSiteContent()
        // call pulls fresh from Firestore and re-persists to localStorage.
        _siteContentCache = null;
        _siteContentCacheTime = 0;
        try {
            if (typeof localStorage !== 'undefined') {
                localStorage.removeItem(SITE_CONTENT_LS_KEY);
            }
        } catch { /* localStorage unavailable */ }
        return true;
    } catch (e) { return false; }
}

export { DEFAULT_SITE_CONTENT };

// ═══════════════════════════════════════════════════════════════
//  PROGRAMS
// ═══════════════════════════════════════════════════════════════
// Schema: { name, description, price, priceLabel, bunnyCollectionId,
//           facebookGroupUrl, imageEmoji, imageUrl, highlights[], details, order, active,
//           programType ('self-paced' | 'cohort'), weeks[], videoOrder[] }
// Weeks schema (cohort only): [{ weekNumber, title, description, videoIds[], releaseDayOffset }]
// videoOrder (self-paced only): [videoId, videoId, ...]

// Generate a URL-friendly slug from a program name
export function generateSlug(name) {
    if (!name) return '';
    return name.toLowerCase()
        .replace(/['']/g, '')           // remove apostrophes/smart quotes
        .replace(/&/g, 'and')           // & → and
        .replace(/[^a-z0-9\s-]/g, '')  // strip non-alphanumeric
        .replace(/\s+/g, '-')           // spaces → hyphens
        .replace(/-+/g, '-')            // collapse multiple hyphens
        .replace(/^-|-$/g, '');         // trim leading/trailing hyphens
}

export async function fetchPrograms() {
    if (!firebaseEnabled) return null;
    try {
        const q = query(collection(db, 'programs'), orderBy('order', 'asc'));
        const snap = await getDocs(q);
        return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (e) { return null; }
}

export async function createProgram(data) {
    if (!firebaseEnabled) return null;
    try {
        const ref = await addDoc(collection(db, 'programs'), { ...data, createdAt: serverTimestamp() });
        return ref.id;
    } catch (e) { return null; }
}

export async function updateProgram(id, data) {
    if (!firebaseEnabled) return false;
    try { await updateDoc(doc(db, 'programs', id), data); return true; }
    catch (e) { return false; }
}

export async function deleteProgram(id) {
    if (!firebaseEnabled) return false;
    try {
        // Cascade: delete all pods belonging to this program
        const podsQuery = query(collection(db, 'pods'), where('programId', '==', id));
        const podsSnap = await getDocs(podsQuery);
        const podDeletes = podsSnap.docs.map((d) => deleteDoc(doc(db, 'pods', d.id)));
        await Promise.all(podDeletes);
        // Clean up orphaned completions for this program
        try {
            const compQuery = query(collection(db, 'completions'), where('programId', '==', id));
            const compSnap = await getDocs(compQuery);
            if (compSnap.size > 0) {
                await Promise.all(compSnap.docs.map((d) => deleteDoc(doc(db, 'completions', d.id))));
                console.log(`Cleaned up ${compSnap.size} completion(s) for deleted program ${id}`);
            }
        } catch (cleanupErr) { console.warn('Non-critical: completions cleanup failed:', cleanupErr); }
        // Delete the program itself
        await deleteDoc(doc(db, 'programs', id));
        return true;
    }
    catch (e) { console.error('Error deleting program:', e); return false; }
}

// ═══════════════════════════════════════════════════════════════
//  WORKOUT COMPLETIONS  (event log — see adr-completions-event-log.md)
// ═══════════════════════════════════════════════════════════════
// Schema: {
//   userId, videoId, videoTitle, programId (optional|null),
//   completedAt (ISO), completedDayCT ('YYYY-MM-DD' America/Chicago),
//   source ('live'|'ondemand'|'manual'|'program'), occurrenceKey (dedup token),
//   createdAt (serverTimestamp)
// }
//
// This collection is an EVENT LOG, not a set: re-doing a workout MUST create a
// new completion so streaks advance and challenges ("move X days / X times this
// month") can count repeats. The dedup below keys on OCCURRENCE (one per playback
// session / one per live broadcast), NEVER on "has this user ever done this
// video" — reverting that reintroduces the frozen-streak bug.
//
// Legacy docs (pre-2026-06-06) lack completedDayCT/source/occurrenceKey; all
// readers derive completedDayCT from completedAt so legacy history keeps counting.

// ── Community counter bump (classes only) ────────────────────────────────
// Fire-and-forget: the completion record is the source of truth and must never
// be delayed or failed by an aggregate update. Defined locally rather than
// imported from communityStats.js, which imports `db` from this module.
function bumpCommunity(delta) {
    if (!delta) return;
    setDoc(
        doc(db, 'communityStats', 'global'),
        { totalClassCompletions: increment(delta), updatedAt: serverTimestamp() },
        { merge: true },
    ).catch((e) => console.error('Error bumping community total:', e));
}

export async function markWorkoutComplete(userId, videoId, videoTitle, programId = null, options = {}) {
    if (!firebaseEnabled || !userId || !videoId) return null;
    const { occurrenceKey = null, source = 'manual' } = options;
    try {
        // ── Idempotency, occurrence-scoped (NOT video-scoped) ──
        if (occurrenceKey) {
            // A retry / double-fire of the SAME session collapses to one doc.
            // Two equality filters use Firestore's automatic indexes (no composite).
            const byKey = await getDocs(query(
                collection(db, 'completions'),
                where('userId', '==', userId),
                where('occurrenceKey', '==', occurrenceKey),
            ));
            if (!byKey.empty) {
                const d0 = byKey.docs[0];
                return { id: d0.id, ...d0.data() };
            }
        } else {
            // Safety net for callers that pass no key: collapse only an accidental
            // double-write within the SAME (video, programId, CT day). A genuine
            // next-day repeat still logs; this never blocks repeats across days.
            const todayKey = trainerDayKey(serverNow());
            const byVideo = await getDocs(query(
                collection(db, 'completions'),
                where('userId', '==', userId),
                where('videoId', '==', videoId),
            ));
            const dupe = byVideo.docs.find((d) => {
                const data = d.data();
                const scopeMatch = programId ? data.programId === programId : !data.programId;
                const dayKey = data.completedDayCT || trainerDayKey(data.completedAt);
                return scopeMatch && dayKey === todayKey;
            });
            if (dupe) return { id: dupe.id, ...dupe.data() };
        }

        // Canonical clock: server-anchored when synced, local otherwise (serverNow
        // degrades to Date.now()). completedDayCT bucketed in America/Chicago.
        const nowMs = serverNow();
        const completedAt = new Date(nowMs).toISOString();
        const completedDayCT = trainerDayKey(nowMs);
        const data = {
            userId,
            videoId,
            videoTitle: videoTitle || '',
            programId: programId || null,
            completedAt,
            completedDayCT,
            source,
            createdAt: serverTimestamp(),
        };
        if (occurrenceKey) data.occurrenceKey = occurrenceKey;
        const ref = await addDoc(collection(db, 'completions'), data);
        // Increment the aggregate userStats counter (fire-and-forget, non-blocking)
        setDoc(doc(db, 'userStats', userId), {
            completionCount: increment(1),
            lastCompletedAt: completedAt,
            lastCompletedDayCT: completedDayCT,
        }, { merge: true }).catch((e) => console.error('Error incrementing userStats:', e));
        // Community total counts CLASSES only — shorts, program videos and
        // Series days are excluded by the same rule the badges use.
        if (isClassCompletion(data)) bumpCommunity(1);
        return { id: ref.id, ...data };
    } catch (e) { console.error('Error marking complete:', e); return null; }
}

// Scoped undo for a single just-logged manual completion (delete by doc id).
// This is the ONLY sanctioned per-session deletion — distinct from
// unmarkWorkoutComplete (which clears ALL sessions of a video in a scope and is
// retained for the legacy toggle + admin tooling). Decrements the aggregate.
export async function deleteCompletionById(userId, completionId) {
    if (!firebaseEnabled || !userId || !completionId) return false;
    try {
        const ref = doc(db, 'completions', completionId);
        const snap = await getDoc(ref);
        if (!snap.exists()) return false;
        // Defensive: never let one user delete another's record from the client.
        if (snap.data().userId !== userId) return false;
        const removed = snap.data();
        await deleteDoc(ref);
        setDoc(doc(db, 'userStats', userId), {
            completionCount: increment(-1),
        }, { merge: true }).catch((e) => console.error('Error decrementing userStats:', e));
        // An undone completion didn't happen — the community total steps back
        // too (unlike a cancelled membership, which never removes history).
        if (isClassCompletion(removed)) bumpCommunity(-1);
        return true;
    } catch (e) { console.error('Error deleting completion:', e); return false; }
}

export async function unmarkWorkoutComplete(userId, videoId, programId = null) {
    if (!firebaseEnabled || !userId || !videoId) return false;
    try {
        // Query scoped to the specific context (program or free)
        const q = query(collection(db, 'completions'), where('userId', '==', userId), where('videoId', '==', videoId));
        const snap = await getDocs(q);
        // Only delete completions matching the same programId context
        const toDelete = snap.docs.filter((d) => {
            const data = d.data();
            if (programId) return data.programId === programId;
            return !data.programId; // free workout: only delete ones with no programId
        });
        await Promise.all(toDelete.map((d) => deleteDoc(doc(db, 'completions', d.id))));
        // Decrement the aggregate userStats counter by the number of docs deleted
        if (toDelete.length > 0) {
            setDoc(doc(db, 'userStats', userId), {
                completionCount: increment(-toDelete.length),
            }, { merge: true }).catch((e) => console.error('Error decrementing userStats:', e));
            const classDeletes = toDelete.filter((d) => isClassCompletion(d.data())).length;
            if (classDeletes > 0) bumpCommunity(-classDeletes);
        }
        return true;
    } catch (e) { console.error('Error unmarking complete:', e); return false; }
}

export async function fetchUserCompletions(userId) {
    if (!firebaseEnabled || !userId) return [];
    try {
        const q = query(collection(db, 'completions'), where('userId', '==', userId), orderBy('completedAt', 'desc'));
        const snap = await getDocs(q);
        return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (e) { console.error('Error fetching completions:', e); return []; }
}

// Resolve the id-backed completion sessions for ONE (user, video) in a given
// scope, newest-first. Used ONLY by the persistent undo affordances (card
// "undo last" + modal "Your history") — fetched just-in-time when a member
// actually opens undo, so the regression-sensitive auto-completion write path
// (which hands the parent only a videoId, no doc id) is never touched. Two
// equality filters use Firestore's AUTOMATIC index (no composite). Scope match
// mirrors unmarkWorkoutComplete: a programId narrows to that program; null
// narrows to programId-less (On Demand / free) docs.
export async function fetchVideoCompletions(userId, videoId, programId = null) {
    if (!firebaseEnabled || !userId || !videoId) return [];
    try {
        const q = query(
            collection(db, 'completions'),
            where('userId', '==', userId),
            where('videoId', '==', videoId),
        );
        const snap = await getDocs(q);
        const list = snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((cmp) => (programId ? cmp.programId === programId : !cmp.programId));
        list.sort((a, b) => {
            const ta = a.completedAt ? new Date(a.completedAt).getTime() : 0;
            const tb = b.completedAt ? new Date(b.completedAt).getTime() : 0;
            return tb - ta; // newest first → [0] is the "last" completion
        });
        return list;
    } catch (e) { console.error('Error fetching video completions:', e); return []; }
}

// Fetch aggregated completion counts from userStats collection (admin only)
// Returns { userId: count } map — same shape as the old fetchCompletionCounts()
export async function fetchAllUserStats() {
    if (!firebaseEnabled) return {};
    try {
        const snap = await getDocs(collection(db, 'userStats'));
        const counts = {};
        snap.docs.forEach((d) => {
            const count = d.data().completionCount || 0;
            if (count > 0) counts[d.id] = count;
        });
        return counts;
    } catch (e) { console.error('Error fetching user stats:', e); return {}; }
}

// Build per-surface completion lookups from a (pre-scoped) completions array.
// Callers pass ONLY the completions relevant to their surface — e.g. ProgramView
// filters to its programId, Workouts/FreeWorkouts filter to programId-less docs —
// so the counts are correct for that context (a video shared across programs can
// carry a different count per program).
//
// Returns:
//   completedIds  Set of videoIds with >=1 completion (drives the ✓ badge/filter)
//   perVideo      { [videoId]: { count, lastCompletedAt } } for the history chip
export function indexCompletions(completions) {
    const completedIds = new Set();
    const perVideo = {};
    (completions || []).forEach((c) => {
        if (!c || !c.videoId) return;
        completedIds.add(c.videoId);
        const cur = perVideo[c.videoId];
        if (!cur) {
            perVideo[c.videoId] = { count: 1, lastCompletedAt: c.completedAt || null };
        } else {
            cur.count += 1;
            if (c.completedAt && (!cur.lastCompletedAt || new Date(c.completedAt) > new Date(cur.lastCompletedAt))) {
                cur.lastCompletedAt = c.completedAt;
            }
        }
    });
    return { completedIds, perVideo };
}

// Compute stats from the completions EVENT LOG. Repeats count (see ADR).
//
// All day bucketing is in America/Chicago (the trainer's calendar) via
// completedDayCT — derived from completedAt for legacy docs. YYYY-MM-DD keys sort
// lexicographically = chronologically, so week/month ranges and the streak walk
// are pure string comparisons (DST-safe, viewer-timezone-independent).
//
// Returns (back-compat keys preserved + challenge primitives added):
//   streak                 consecutive CT days w/ >=1 completion, 1-day grace
//   thisWeek               total sessions this CT week (Sun-start) — repeat-aware
//   total                  total lifetime sessions
//   activeDaysThisWeek     distinct CT days moved this week  ("move X days")
//   activeDaysThisMonth    distinct CT days moved this month
//   totalSessionsThisMonth total sessions this CT month       ("X times this month")
//   perVideo               { [videoId]: { count, lastCompletedAt } } for history UI
//   programs               passthrough
export function computeStats(completions, purchasedCount) {
    const list = Array.isArray(completions) ? completions : [];
    const dayKeyOf = (c) => c.completedDayCT || trainerDayKey(c.completedAt);

    const todayKey = trainerDayKey(serverNow());
    const monthPrefix = todayKey.slice(0, 7); // 'YYYY-MM'

    // Sunday-start of the current CT week, as a YYYY-MM-DD key.
    const weekStartKey = (() => {
        const [y, m, d] = todayKey.split('-').map(Number);
        const dt = new Date(Date.UTC(y, m - 1, d, 12));
        dt.setUTCDate(dt.getUTCDate() - dt.getUTCDay()); // back to Sunday
        const yy = dt.getUTCFullYear();
        const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
        const dd = String(dt.getUTCDate()).padStart(2, '0');
        return `${yy}-${mm}-${dd}`;
    })();

    const daySet = new Set();
    const weekDays = new Set();
    const monthDays = new Set();
    const perVideo = {};
    let sessionsThisWeek = 0;
    let totalSessionsThisMonth = 0;

    list.forEach((c) => {
        const k = dayKeyOf(c);
        if (!k) return;
        daySet.add(k);
        if (k >= weekStartKey && k <= todayKey) { sessionsThisWeek += 1; weekDays.add(k); }
        if (k.slice(0, 7) === monthPrefix) { totalSessionsThisMonth += 1; monthDays.add(k); }

        const vid = c.videoId;
        if (vid) {
            const cur = perVideo[vid];
            if (!cur) {
                perVideo[vid] = { count: 1, lastCompletedAt: c.completedAt };
            } else {
                cur.count += 1;
                if (c.completedAt && (!cur.lastCompletedAt || new Date(c.completedAt) > new Date(cur.lastCompletedAt))) {
                    cur.lastCompletedAt = c.completedAt;
                }
            }
        }
    });

    const base = {
        thisWeek: sessionsThisWeek,
        total: list.length,
        activeDaysThisWeek: weekDays.size,
        activeDaysThisMonth: monthDays.size,
        totalSessionsThisMonth,
        todayActive: daySet.has(todayKey), // false ⇒ streak (if any) is "held by grace"
        perVideo,
        programs: purchasedCount,
    };

    // Streak: walk backward from today (CT), with a 1-day grace so a member who
    // hasn't moved YET today but moved yesterday still sees their streak "held".
    let cursor = todayKey;
    if (!daySet.has(cursor)) {
        cursor = previousDayKey(cursor); // grace day
        if (!daySet.has(cursor)) return { ...base, streak: 0 };
    }
    let streak = 0;
    for (let i = 0; i < 366 && daySet.has(cursor); i += 1) {
        streak += 1;
        cursor = previousDayKey(cursor);
    }
    return { ...base, streak };
}

// ═══════════════════════════════════════════════════════════════
//  PODS (cohort-based programs)
// ═══════════════════════════════════════════════════════════════
// Schema: { programId, podName, startDate (ISO), lockDate (ISO), saleStartDate (ISO, optional),
//           maxParticipants, participants: [userId], waitlist: [{userId, email, joinedAt}],
//           status ('upcoming'|'active'|'completed'), salesModel ('immediate'|'waitlist'),
//           waitlistEmailSubject, waitlistEmailBody,
//           facebookGroupUrl (override), meetings: [{weekNumber, date, time, zoomLink, title}],
//           createdAt }

export async function fetchPods(programId) {
    if (!firebaseEnabled) return [];
    try {
        const q = programId
            ? query(collection(db, 'pods'), where('programId', '==', programId), orderBy('startDate', 'asc'))
            : query(collection(db, 'pods'), orderBy('startDate', 'asc'));
        const snap = await getDocs(q);
        return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (e) { console.error('Error fetching pods:', e); return []; }
}

export async function createPod(data) {
    if (!firebaseEnabled) return null;
    try {
        const ref = await addDoc(collection(db, 'pods'), {
            ...data,
            participants: data.participants || [],
            status: data.status || 'upcoming',
            createdAt: serverTimestamp(),
        });
        return ref.id;
    } catch (e) { console.error('Error creating pod:', e); return null; }
}

export async function updatePod(id, data) {
    if (!firebaseEnabled) return false;
    try { await updateDoc(doc(db, 'pods', id), data); return true; }
    catch (e) { console.error('Error updating pod:', e); return false; }
}

// ── Pod membership changes from Admin (2026-10-06) ──────────────────────────
// Admin used to save a pod by writing the WHOLE participants array it loaded
// when the editor opened. Anyone who joined meanwhile (a purchase, a claim, the
// purchase-link sweep) was silently removed by that save. These apply only the
// admin's CHANGES to the pod's CURRENT list, inside a transaction.
export async function applyPodMembershipDelta(podId, add = [], remove = []) {
    if (!firebaseEnabled || !podId) return null;
    try {
        return await runTransaction(db, async (tx) => {
            const ref = doc(db, 'pods', podId);
            const snap = await tx.get(ref);
            if (!snap.exists()) throw new Error('pod-missing');
            const next = (snap.data().participants || []).filter((id) => !remove.includes(id));
            add.forEach((id) => { if (!next.includes(id)) next.push(id); });
            tx.update(ref, { participants: next });
            return next;
        });
    } catch (e) { console.error('Error applying pod membership change:', e); return null; }
}

// Move a member between pods in ONE transaction: removed from the old pod and
// added to the new one together (previously the removal waited for "Save Pod",
// so leaving without saving left the member in both pods).
export async function movePodMember(userId, fromPodId, toPodId) {
    if (!firebaseEnabled || !userId || !toPodId) return false;
    try {
        await runTransaction(db, async (tx) => {
            const toRef = doc(db, 'pods', toPodId);
            const fromRef = fromPodId ? doc(db, 'pods', fromPodId) : null;
            const toSnap = await tx.get(toRef);
            const fromSnap = fromRef ? await tx.get(fromRef) : null;
            if (!toSnap.exists()) throw new Error('target-pod-missing');
            const toParts = toSnap.data().participants || [];
            if (!toParts.includes(userId)) tx.update(toRef, { participants: [...toParts, userId] });
            if (fromSnap && fromSnap.exists()) {
                tx.update(fromRef, { participants: (fromSnap.data().participants || []).filter((id) => id !== userId) });
            }
        });
        return true;
    } catch (e) { console.error('Error moving pod member:', e); return false; }
}

export async function deletePod(id) {
    if (!firebaseEnabled) return false;
    try { await deleteDoc(doc(db, 'pods', id)); return true; }
    catch (e) { console.error('Error deleting pod:', e); return false; }
}

export async function joinPod(podId, userId) {
    if (!firebaseEnabled || !podId || !userId) return false;
    try {
        const podRef = doc(db, 'pods', podId);
        const result = await runTransaction(db, async (transaction) => {
            const snap = await transaction.get(podRef);
            if (!snap.exists()) return false;
            const pod = snap.data();
            if (pod.participants?.includes(userId)) return true; // already in
            if (pod.participants?.length >= pod.maxParticipants) return false; // full
            const now = new Date().toISOString();
            if (pod.lockDate && now >= pod.lockDate) return false; // locked
            // Check if sale has started (if waitlist model)
            if (pod.salesModel === 'waitlist' && pod.saleStartDate && now < pod.saleStartDate) return false;
            const updated = [...(pod.participants || []), userId];
            transaction.update(podRef, { participants: updated });
            return true;
        });
        return result;
    } catch (e) { console.error('Error joining pod:', e); return false; }
}

export async function joinWaitlist(podId, userId, email) {
    if (!firebaseEnabled || !podId || !userId) return false;
    // Transaction (2026-10-06): this used to read the list, add to it, and save
    // the WHOLE list back — two members joining in the same moment meant the
    // second save erased the first. The transaction re-reads and retries on a
    // conflict, so every join and leave lands. Same entry shape as before.
    try {
        const podRef = doc(db, 'pods', podId);
        return await runTransaction(db, async (tx) => {
            const snap = await tx.get(podRef);
            if (!snap.exists()) return false;
            const waitlist = [...(snap.data().waitlist || [])];
            if (waitlist.some((w) => w && w.userId === userId)) return true; // already on waitlist
            waitlist.push({ userId, email: email || '', joinedAt: new Date().toISOString() });
            tx.update(podRef, { waitlist });
            return true;
        });
    } catch (e) { console.error('Error joining waitlist:', e); return false; }
}

export async function leaveWaitlist(podId, userId) {
    if (!firebaseEnabled || !podId || !userId) return false;
    try {
        const podRef = doc(db, 'pods', podId);
        return await runTransaction(db, async (tx) => {
            const snap = await tx.get(podRef);
            if (!snap.exists()) return false;
            const current = snap.data().waitlist || [];
            const waitlist = current.filter((w) => !(w && w.userId === userId));
            if (waitlist.length !== current.length) tx.update(podRef, { waitlist });
            return true;
        });
    } catch (e) { console.error('Error leaving waitlist:', e); return false; }
}

// Get the user's pod for a given program
export function getUserPod(pods, userId) {
    if (!pods || !userId) return null;
    return pods.find((p) => p.participants?.includes(userId)) || null;
}

// Get ALL pods the user belongs to (for multi-cohort re-enrollment)
export function getUserPods(pods, userId) {
    if (!pods || !userId) return [];
    return pods.filter((p) => p.participants?.includes(userId));
}

// Check if user is on any waitlist for this program's pods
export function getUserWaitlistPod(pods, userId) {
    if (!pods || !userId) return null;
    return pods.find((p) => p.waitlist?.some((w) => w.userId === userId)) || null;
}

// Get pod sale status
export function getPodSaleStatus(pod) {
    const now = new Date().toISOString();
    const isFull = pod.participants?.length >= pod.maxParticipants;
    const isLocked = pod.lockDate && now >= pod.lockDate;
    if (isFull || isLocked || pod.status === 'completed') return 'closed';
    if (pod.salesModel === 'waitlist' && pod.saleStartDate) {
        if (now < pod.saleStartDate) return 'waitlist'; // before sale opens
        return 'on-sale'; // sale is live
    }
    return 'immediate'; // no sale date, buy anytime
}

// Get available (not full, not locked) pods — includes waitlist pods
export function getAvailablePods(pods) {
    const now = new Date().toISOString();
    return pods.filter((p) => {
        const isFull = p.participants?.length >= p.maxParticipants;
        const isLocked = p.lockDate && now >= p.lockDate;
        return !isFull && !isLocked && p.status !== 'completed';
    });
}

// Build mailto link for emailing a group
export function buildMailtoLink({ to = [], bcc = [], subject = '', body = '', from = '' }) {
    const params = [];
    if (bcc.length > 0) params.push('bcc=' + encodeURIComponent(bcc.join(',')));
    if (subject) params.push('subject=' + encodeURIComponent(subject));
    if (body) params.push('body=' + encodeURIComponent(body));
    const toStr = to.length > 0 ? to.join(',') : '';
    return 'mailto:' + encodeURIComponent(toStr) + (params.length > 0 ? '?' + params.join('&') : '');
}

// Compute which weeks are unlocked for a user's pod
export function getUnlockedWeeks(program, pod) {
    if (!program?.weeks || !pod?.startDate) return [];
    const now = new Date();
    const start = new Date(pod.startDate);
    const daysSinceStart = Math.floor((now - start) / (1000 * 60 * 60 * 24));
    if (daysSinceStart < 0) return []; // program hasn't started
    return program.weeks.filter((w) => (w.releaseDayOffset || 0) <= daysSinceStart);
}

// ── Waiver functions ────────────────────────────────────────
export async function checkWaiverSigned(userId, programId) {
    if (!db || !userId || !programId) return false;
    try {
        const q = query(collection(db, 'waivers'), where('userId', '==', userId), where('programId', '==', programId));
        const snap = await getDocs(q);
        return !snap.empty;
    } catch (e) { console.error('Error checking waiver:', e); return false; }
}

// `hasAnyWaiver` was retired 2026-05-05 along with WaiverGate / WaiverModal.
// Express consent is now captured inline at signup via Clerk's "Require
// express consent" checkbox; the audit record is written by `api/clerk-webhook.js`
// on the `user.created` event. There is no app-level gate that needs to query
// for existing waivers anymore. See `docs/decisions/adr-waiver-clerk-express-consent.md`.

export async function fetchWaivers() {
    if (!db) return [];
    try {
        const snap = await getDocs(collection(db, 'waivers'));
        return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (e) { console.error('Error fetching waivers:', e); return []; }
}